import { useTranslation } from 'next-i18next'
import { type FC, useMemo } from 'react'
import { useTheme } from 'styled-components'

import { VipSectionContainer } from '@/components/vip/shared/VipSectionContainer'
import { VipAttributesWrapper } from '@/components/vip/vip-attributes/styled'
import { VipAttributeCore } from '@/components/vip/vip-attributes/VipAttributeCore'
import { VipAttributesBody } from '@/components/vip/vip-attributes/VipAttributesBody'
import { TRANSLATION_KEYS } from '@/constants/localization'
import { filterNullAttributes } from '@/features/attributes/utils/filterNullAttributes'
import {
  getAttributeCoreToDisplay,
  getAttributeGroupsToDisplay,
  getAttributeToDisplay,
} from '@/features/attributes/utils/getDisplayableAttributes'
import { type VipQueryAttributeAll } from '@/types/listings'

export type VipAttributesSectionProps = {
  attributes: VipQueryAttributeAll
  categoryId: number
}

export const VipAttributesSection: FC<VipAttributesSectionProps> = ({ attributes, categoryId }) => {
  const { t } = useTranslation([TRANSLATION_KEYS.LISTING])
  const { spacing } = useTheme()
  const filteredAttributes = filterNullAttributes(attributes)

  const coreAttributes = useMemo(
    () => getAttributeCoreToDisplay(t, categoryId, filteredAttributes),
    [t, categoryId, filteredAttributes]
  )

  const hasCoreAttributes = !!coreAttributes.length

  const groupAttributes = useMemo(
    () => getAttributeGroupsToDisplay(t, categoryId, filteredAttributes),
    [t, categoryId, filteredAttributes]
  )

  const genericAttributes = useMemo(
    () =>
      getAttributeToDisplay(t, filteredAttributes, { additionalOptions: true, forSaleBy: true }),
    [t, filteredAttributes]
  )

  /**
   * It should not return section if there are no displayable attributes
   * */
  if (!groupAttributes.length && !genericAttributes.length && !hasCoreAttributes) {
    return null
  }

  return (
    <VipSectionContainer
      pBottom={spacing.husky}
      pTop={spacing.husky}
      data-testid="vip-attributes-section"
    >
      <VipAttributesWrapper hasCoreAttributes={hasCoreAttributes}>
        {coreAttributes ? <VipAttributeCore coreAttributes={coreAttributes} /> : null}

        <VipAttributesBody
          groupAttributes={groupAttributes}
          genericAttributes={genericAttributes}
          categoryId={categoryId}
          hasCoreAttributes={hasCoreAttributes}
        />
      </VipAttributesWrapper>
    </VipSectionContainer>
  )
}
